// Şirket adı normalizasyonu + bulanık benzerlik (saf, platform-agnostik).
//
// Kaynağı `services/report-import/suggest.ts`'ti (kapak önerisinin müşteri eşlemesi);
// toplu ekranın satır içi "+ Yeni müşteri" akışı aynı ölçüyü İSTEMCİ tarafında da
// kullandığı için (benzer kayıt uyarısı + aynı adın satırlara yayılması) buraya taşındı.
// İki kopya ölçü = iki farklı "benzer" tanımı demek olurdu; backend `suggest.ts` bu
// dosyadan okur ve geriye uyum için yeniden export eder.

/** Türkçe harfleri ASCII'ye indirger — `normalizeForGrounding` ile aynı ruh. */
function foldTurkish(s: string): string {
  return s
    .replace(/[İIı]/g, "i")
    .replace(/[Şş]/g, "s")
    .replace(/[Ğğ]/g, "g")
    .replace(/[Üü]/g, "u")
    .replace(/[Öö]/g, "o")
    .replace(/[Çç]/g, "c");
}

/** Tüzel kişilik ekleri — eşleşmede gürültü yaratır, düşürülür. */
const LEGAL_SUFFIX_TOKENS = new Set([
  "as",
  "tas",
  "ltd",
  "sti",
  "san",
  "sanayi",
  "tic",
  "ticaret",
  "holding",
  "anonim",
  "sirketi",
  "limited",
  "ve",
  "grup",
  "group",
  "inc",
  "co",
]);

/**
 * "LİLA KAĞIT SAN. ve TİC. A.Ş." → "lila kagit".
 * Noktalama düşer, Türkçe harfler katlanır, tüzel ekler atılır. Tek harflik
 * parçalar ("A.Ş." → "a", "s") de düşer — kısaltma kalıntısıdır, ayırt etmez.
 */
export function normalizeCompanyName(raw: string): string {
  const folded = foldTurkish(raw).toLowerCase();
  const tokens = folded
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1)
    .filter((t) => !LEGAL_SUFFIX_TOKENS.has(t));
  return tokens.join(" ");
}

/** Klasik Levenshtein mesafesi (küçük dizeler — tam matris yeterli). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost);
    }
    prev = cur;
  }
  return prev[b.length]!;
}

/** Marka parçası sayılmak için gereken en küçük uzunluk ("gyo", "as" ayırt etmez). */
const BRAND_TOKEN_MIN = 4;
/** Aynı marka parçasıyla başlayan iki adın taban benzerliği. */
const SAME_BRAND_SCORE = 0.75;

/**
 * Marka SAYILMAYAN ilk parçalar (inceleme bulgusu): coğrafya/jenerik önekler çok
 * firmada ortaktır ve tek başına eşleşme sayılırsa "Türkiye İş Bankası" ↔ "Türkiye
 * Sigorta" benzer görünür — uyarı satırındaki adlar tıklanabilir olduğu için bu
 * yanlış pozitif yanlış müşteri seçimine yol açabilir. Bu parçalarda prim yalnız
 * İKİNCİ bir ortak parça varsa uygulanır.
 */
const GENERIC_FIRST_TOKENS = new Set([
  "turkiye",
  "turk",
  "anadolu",
  "istanbul",
  "ankara",
  "izmir",
  "ege",
  "marmara",
  "akdeniz",
  "karadeniz",
  "global",
  "international",
  "euro",
  "avrupa",
  "yeni",
  "milli",
  "ulusal",
]);

/**
 * İki şirket adının benzerliği (0–1). Normalize edilmiş adlar birbirini KAPSIYORSA 1,
 * aksi halde `1 - levenshtein / enUzun`. Adlardan biri normalize sonrası boşsa 0.
 *
 * `matchClientId` eşiği (Levenshtein ≤2) "aynı kayıt" kadar katıdır; bu oran
 * "benzer kayıt" uyarısı için daha geniş bir ölçü verir.
 *
 * Düzenleme mesafesi tek başına yetmiyor: Türkçe unvanlarda marka başta durur ve
 * gerisi uzar ("Sinpaş GYO" ↔ "Sinpaş Yapı Endüstri") — mesafe oranı 0,3'e düşer
 * ama kullanıcının görmesi gereken tam da bu kayıttır. Bu yüzden İLK parça aynıysa
 * (≥4 harf) benzerlik en az `SAME_BRAND_SCORE` olur; ilk parça jenerik/coğrafi ise
 * (`GENERIC_FIRST_TOKENS`) prim için İKİNCİ bir ortak parça (≥4 harf) şarttır.
 */
export function companyNameSimilarity(a: string, b: string): number {
  const na = normalizeCompanyName(a);
  const nb = normalizeCompanyName(b);
  if (na.length === 0 || nb.length === 0) return 0;
  if (na === nb || na.includes(nb) || nb.includes(na)) return 1;
  const longest = Math.max(na.length, nb.length);
  const byDistance = Math.max(0, 1 - levenshtein(na, nb) / longest);
  const tokensA = na.split(" ");
  const tokensB = nb.split(" ");
  const brandA = tokensA[0]!;
  let sameBrand = brandA === tokensB[0] && brandA.length >= BRAND_TOKEN_MIN;
  if (sameBrand && GENERIC_FIRST_TOKENS.has(brandA)) {
    const restB = new Set(tokensB.slice(1));
    sameBrand = tokensA.slice(1).some((t) => t.length >= BRAND_TOKEN_MIN && restB.has(t));
  }
  return Math.max(byDistance, sameBrand ? SAME_BRAND_SCORE : 0);
}

/** "Benzer kayıt" sayılma eşiği — `companyNameSimilarity` ölçüsünde. */
export const COMPANY_NAME_SIMILAR_MIN = 0.6;
